import { useTranslation } from "react-i18next"
import { VariantProps } from "class-variance-authority"
import { buttonVariants } from "@/components/ui/button"
import { useUserData } from "@/hooks/useUserData"
import { requestTakeOverDialog_patientFileLockFragment$data } from "@/modules/common/dialog/__generated__/requestTakeOverDialog_patientFileLockFragment.graphql"
import { PatientFileLockChange } from "@/modules/common/dialog/request-take-over-dialog"
import { PatientFileLockAction } from "@/modules/patients/services/__generated__/usePatientFileLockChangeSubscription.graphql"
import { useAllowTakeOverPatientMutation } from "@/modules/patients/services/useAllowTakeOverPatientMutation"
import { useCancelRequestTakeOverPatientMutation } from "@/modules/patients/services/useCancelRequestTakeOverPatientMutation"
import { useDenyTakeOverPatientMutation } from "@/modules/patients/services/useDenyTakeOverPatientMutation"

type DialogType = {
  description: string
  title?: string
  actions: {
    action: "close" | "confirm"
    label: string
    handler: () => void
    variant: VariantProps<typeof buttonVariants>["variant"]
  }[]
}

const useRequestTakeOverDialog: (
  status: PatientFileLockAction | undefined,
  patientFileLockChange: PatientFileLockChange | undefined,
  patientName: string,
  patientId: string,
  patientFileLock: requestTakeOverDialog_patientFileLockFragment$data | null | undefined,
  isInboundCall?: boolean,
) => DialogType = (status, patientFileLockChange, patientName, patientId, patientFileLock) => {
  const [t] = useTranslation()
  const takeOverRequestCountdownValue = patientFileLockChange?.takeOverRequestCountdownValue
  const { firstName: lockByFirstName, lastName: lockByLastName } = useUserData(patientFileLock?.lockedBy) || {}

  const { firstName: lockRequestedByFirstName, lastName: lockRequestedByLastName } =
    useUserData(patientFileLock?.lockRequestedBy) || {}

  const lockedByName = `${lockByFirstName} ${lockByLastName}`
  const lockRequestedByName = `${lockRequestedByFirstName} ${lockRequestedByLastName}`

  const takeOverAllowed = useAllowTakeOverPatientMutation()
  const takeOverDenied = useDenyTakeOverPatientMutation()
  const cancelRequestTakeOverPatient = useCancelRequestTakeOverPatientMutation()

  switch (status) {
    case "REQUESTED":
      return {
        title: t("patient.takeOver.REQUESTED.title", {
          lockedBy: lockedByName,
        }),
        description: t("patient.takeOver.REQUESTED.description", {
          count: takeOverRequestCountdownValue!,
        }),
        actions: [
          {
            action: "close",
            label: t("common.button.cancelRequest"),
            handler: () => {
              cancelRequestTakeOverPatient({ patientId })
            },
            variant: "secondary",
          },
        ],
      }
    case "REQUEST_COUNTDOWN":
      return {
        title: t("patient.takeOver.REQUEST_COUNTDOWN.title", {
          lockRequestedBy: lockRequestedByName,
          patientName,
        }),
        description: t("patient.takeOver.REQUEST_COUNTDOWN.description", {
          count: takeOverRequestCountdownValue!,
        }),
        actions: [
          {
            action: "close",
            label: t("common.button.deny"),
            handler: () => {
              takeOverDenied({ patientId })
            },
            variant: "secondary",
          },
          {
            action: "confirm",
            label: t("common.button.allow"),
            handler: () => {
              takeOverAllowed({ patientId })
            },
            variant: "primary",
          },
        ],
      }

    case "ALLOWED":
    case "CANCEL_REQUEST":
    case "DENIED":
    case "RELEASED":
    case "TAKEN":
    case undefined:
    default:
      return {
        title: "",
        description: "",
        actions: [],
      }
  }
}

export { useRequestTakeOverDialog }
